import { SuggestionPrestation, SuggestionSection } from '../types/SuggestionTypes';
import { Block, isPrestation } from '../types';

// ── Pool de prestations mock ──────────────────────────────────────────────────

const PRESTATION_POOL: SuggestionPrestation[] = [
  { id: 's-carrelage',   intitule: 'Carrelage sol',               categorie: 'Revêtements',  unite: 'm²', prixUnitaire: '65',  quantite: '1', prixTotal: '65',  chantier: 'Intérieur', fournisseur: 'À définir' },
  { id: 's-faux-plaf',   intitule: 'Faux plafond BA13',           categorie: 'Plâtrerie',    unite: 'm²', prixUnitaire: '42',  quantite: '1', prixTotal: '42',  chantier: 'Intérieur', fournisseur: 'À définir' },
  { id: 's-peinture',    intitule: 'Peinture acrylique murs',     categorie: 'Peinture',     unite: 'm²', prixUnitaire: '22',  quantite: '1', prixTotal: '22',  chantier: 'Intérieur', fournisseur: 'À définir' },
  { id: 's-plomberie',   intitule: 'Plomberie sanitaires',        categorie: 'Plomberie',    unite: 'ens', prixUnitaire: '3500', quantite: '1', prixTotal: '3500', chantier: 'Intérieur', fournisseur: 'À définir' },
  { id: 's-elec',        intitule: 'Tableau électrique + câblage', categorie: 'Électricité', unite: 'ens', prixUnitaire: '4200', quantite: '1', prixTotal: '4200', chantier: 'Intérieur', fournisseur: 'À définir' },
  { id: 's-cvc',         intitule: 'Climatisation split system',  categorie: 'CVC',          unite: 'u',  prixUnitaire: '1800', quantite: '1', prixTotal: '1800', chantier: 'Intérieur', fournisseur: 'À définir' },
  { id: 's-menuint',     intitule: 'Menuiseries intérieures',     categorie: 'Menuiserie',   unite: 'u',  prixUnitaire: '450', quantite: '1', prixTotal: '450', chantier: 'Intérieur', fournisseur: 'À définir' },
  { id: 's-menuext',     intitule: 'Menuiseries extérieures ALU', categorie: 'Menuiserie',   unite: 'u',  prixUnitaire: '1200', quantite: '1', prixTotal: '1200', chantier: 'Façade', fournisseur: 'À définir' },
  { id: 's-ravalement',  intitule: 'Ravalement de façade',        categorie: 'Ravalement',   unite: 'm²', prixUnitaire: '85',  quantite: '1', prixTotal: '85',  chantier: 'Façade', fournisseur: 'À définir' },
  { id: 's-etancheite',  intitule: 'Étanchéité toiture terrasse', categorie: 'Étanchéité',   unite: 'm²', prixUnitaire: '95',  quantite: '1', prixTotal: '95',  chantier: 'Toiture', fournisseur: 'À définir' },
  { id: 's-charpente',   intitule: 'Charpente bois',              categorie: 'Charpente',    unite: 'ml', prixUnitaire: '180', quantite: '1', prixTotal: '180', chantier: 'Toiture', fournisseur: 'À définir' },
  { id: 's-isolation',   intitule: 'Isolation thermique ITI',     categorie: 'Isolation',    unite: 'm²', prixUnitaire: '55',  quantite: '1', prixTotal: '55',  chantier: 'Intérieur', fournisseur: 'À définir' },
  { id: 's-gros-oe',     intitule: 'Démolition cloisons',         categorie: 'Gros œuvre',   unite: 'm²', prixUnitaire: '35',  quantite: '1', prixTotal: '35',  chantier: 'Structure', fournisseur: 'À définir' },
  { id: 's-serrurerie',  intitule: 'Serrurerie / garde-corps',    categorie: 'Serrurerie',   unite: 'ml', prixUnitaire: '320', quantite: '1', prixTotal: '320', chantier: 'Façade', fournisseur: 'À définir' },
  { id: 's-parquet',     intitule: 'Parquet flottant',            categorie: 'Revêtements',  unite: 'm²', prixUnitaire: '48',  quantite: '1', prixTotal: '48',  chantier: 'Intérieur', fournisseur: 'À définir' },
];

// Règles de complémentarité par catégorie
const COMPLEMENTS: Record<string, string[]> = {
  'Électricité': ['s-plomberie', 's-cvc', 's-faux-plaf'],
  'Plomberie':   ['s-elec', 's-cvc', 's-carrelage'],
  'CVC':         ['s-elec', 's-plomberie', 's-isolation'],
  'Peinture':    ['s-carrelage', 's-faux-plaf', 's-menuint'],
  'Plâtrerie':   ['s-peinture', 's-carrelage', 's-menuint'],
  'Revêtements': ['s-peinture', 's-faux-plaf', 's-parquet'],
  'Menuiserie':  ['s-ravalement', 's-serrurerie', 's-peinture'],
  'Ravalement':  ['s-menuext', 's-etancheite', 's-serrurerie'],
  'Étanchéité':  ['s-charpente', 's-ravalement', 's-isolation'],
  'Isolation':   ['s-cvc', 's-faux-plaf', 's-peinture'],
  'Gros œuvre':  ['s-isolation', 's-carrelage', 's-faux-plaf'],
};

function pickIds(ids: string[], n: number): SuggestionPrestation[] {
  return ids
    .map(id => PRESTATION_POOL.find(p => p.id === id))
    .filter((p): p is SuggestionPrestation => !!p)
    .slice(0, n);
}

/**
 * Service de suggestions — retourne 3 prestations contextuelles selon les blocs existants.
 * Conçu pour être remplacé par un appel LLM plus tard.
 */
export class SuggestionService {
  async generateSuggestions(blocks: Block[] = []): Promise<SuggestionPrestation[]> {
    // Catégories déjà présentes
    const presentIds = new Set(
      blocks.filter(isPrestation).map(b => b.content.categorie?.toLowerCase() ?? '')
    );

    // Cherche la première règle de complémentarité qui matche
    for (const [cat, complement] of Object.entries(COMPLEMENTS)) {
      if (presentIds.has(cat.toLowerCase())) {
        // Filtre les ids déjà présents dans la liste
        const filtered = complement.filter(id => {
          const p = PRESTATION_POOL.find(x => x.id === id);
          return p && !presentIds.has(p.categorie.toLowerCase());
        });
        if (filtered.length >= 2) return pickIds(filtered, 3);
      }
    }

    // Défaut : 3 premières du pool non déjà présentes
    const defaults = PRESTATION_POOL.filter(
      p => !presentIds.has(p.categorie.toLowerCase())
    ).slice(0, 3);
    return defaults.length >= 1 ? defaults : PRESTATION_POOL.slice(0, 3);
  }

  async generateSectionSuggestions(): Promise<SuggestionSection[]> {
    return [
      { id: 'sec-1', titre: 'LOT EXTÉRIEURS',   description: 'Aménagements extérieurs', categorie: 'Extérieur', icon: '🌳' },
      { id: 'sec-2', titre: 'LOT ÉQUIPEMENTS',  description: 'Équipements techniques',  categorie: 'Équipements', icon: '⚙️' },
      { id: 'sec-3', titre: 'LOT SÉCURITÉ',     description: 'Sécurité / accessibilité', categorie: 'Sécurité', icon: '🔒' },
    ];
  }
}

export const suggestionService = new SuggestionService();
